import type { PartnerAssistantEvent } from "./types";

export interface StreamOutcome {
  status: number;
  error: string | null;
  /** Поток кончился событием done или blocked. */
  finished: boolean;
  /** Планы, по которым пришёл plan_status. */
  planStatuses: Set<string>;
  /** Этот поток прерван: новым потоком, «Новым диалогом» или закрытием панели. */
  aborted: boolean;
}

type Run = (
  onEvent: (e: PartnerAssistantEvent) => void,
  signal: AbortSignal
) => Promise<{ status: number; error: string | null }>;

/**
 * Один активный поток помощника: новый прерывает прежний. Всё, что
 * относится к вызову (сигнал, исключение, итог), берётся из его
 * собственного контроллера, поэтому прерванный поток не трогает ленту
 * нового хода: его события отбрасываются, а итог помечен aborted.
 */
export function createStreamRunner() {
  let active: AbortController | null = null;

  const run = async (
    fn: Run,
    dispatchEvent: (e: PartnerAssistantEvent) => void
  ): Promise<StreamOutcome> => {
    active?.abort();
    const controller = new AbortController();
    active = controller;
    let finished = false;
    const planStatuses = new Set<string>();
    const onEvent = (event: PartnerAssistantEvent) => {
      if (controller.signal.aborted) return;
      if (event.type === "done" || event.type === "blocked") finished = true;
      if (event.type === "plan_status") planStatuses.add(event.planId);
      dispatchEvent(event);
    };
    let result: { status: number; error: string | null };
    try {
      result = await fn(onEvent, controller.signal);
    } catch {
      result = { status: 0, error: "NETWORK" };
    } finally {
      if (active === controller) active = null;
    }
    return {
      ...result,
      finished,
      planStatuses,
      aborted: controller.signal.aborted,
    };
  };

  const abort = () => {
    active?.abort();
    active = null;
  };

  return { run, abort };
}
